import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ImpersonationProvider, useActiveOrganization } from '@center-technologies/shared-api-clients';
import type { RootState } from '@center-pwa/store/store';

/**
 * Feeds the signed-in session's own acting scope into `ImpersonationProvider`.
 *
 * Every org/user-scoped hook in the design system reads its scope from
 * `useImpersonation().impersonationDetails`. Under B2C that was always populated,
 * because the PWA minted a "self-impersonation" token for the user's own
 * center_user at start-up. Under Descope that mint is gone (specs/descope-poc.md):
 * the organization is the tab's active organization (`useActiveOrganization`), and the
 * acting center_user is resolved into `userProfile.userId` during initialization.
 * Without this bridge every schema-type lookup, marketplace read and template
 * picker waited on an organization id that never arrived.
 */
export const SessionScopedImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { activeOrganizationId: organizationId } = useActiveOrganization();
  const userId = useSelector((state: RootState) => state.userProfile?.userId) ?? null;
  const sessionScope = useMemo(() => ({ organizationId, userId }), [organizationId, userId]);
  return <ImpersonationProvider sessionScope={sessionScope}>{children}</ImpersonationProvider>;
};
